// /settings → "Inbound Goods урсгал" tab (spec §2.7a item 9 + §27.15 / Q16).
// Backed by the same governance-config endpoint — only the inboundFlow
// JSON sub-object is touched here.

import { Card, Group, Skeleton, Stack, Switch, Text, Title } from "@mantine/core"
import { useForm } from "@mantine/form"
import { TrayArrowDownIcon } from "@phosphor-icons/react"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import type { IGovernanceConfig } from "../../apis/governance-config"
import { useGovernanceConfigQuery, useSettingsMutations } from "./hooks"
import { ListState } from "../ui"
import { SETTINGS_WRITE, SettingsButton, SettingsFields } from "./settings-controls"
import { HelpIcon } from "@dehub/theme"

export function InboundFlowTab() {
  const { t } = useTranslation("settings")
  const { data, isLoading, error, mutate: reload } = useGovernanceConfigQuery()
  const mutations = useSettingsMutations()

  const form = useForm({
    initialValues: {
      autoCreateStoOnShortage: false,
      externalPickupEnabled: false,
      requireSenderVerifyAtExternalParty: false,
      catalogAutoCreateOnReceipt: false,
      requireManagerApprovalForInbound: false,
      crossWarehouseSourcingEnabled: false,
    },
  })

  useEffect(() => {
    if (!data) return
    const ib = data.inboundFlow
    form.setValues({
      autoCreateStoOnShortage:           ib?.autoCreateStoOnShortage ?? false,
      externalPickupEnabled:             ib?.externalPickupEnabled ?? false,
      requireSenderVerifyAtExternalParty: ib?.requireSenderVerifyAtExternalParty ?? false,
      catalogAutoCreateOnReceipt:        ib?.catalogAutoCreateOnReceipt ?? false,
      requireManagerApprovalForInbound:  ib?.requireManagerApprovalForInbound ?? false,
      crossWarehouseSourcingEnabled:     ib?.crossWarehouseSourcing?.enabled ?? false,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (isLoading) return <Skeleton height={180} radius="md" />
  if (error) {
    return (
      <ListState state="error" title={t("common.error")} description={t("common.loadError")} onRetry={() => reload()} />
    )
  }

  const handleSave = () => {
    const prev = (data as IGovernanceConfig).inboundFlow
    mutations.governance.update({
      inboundFlow: {
        ...prev,
        autoCreateStoOnShortage:           form.values.autoCreateStoOnShortage,
        externalPickupEnabled:             form.values.externalPickupEnabled,
        requireSenderVerifyAtExternalParty: form.values.requireSenderVerifyAtExternalParty,
        catalogAutoCreateOnReceipt:        form.values.catalogAutoCreateOnReceipt,
        requireManagerApprovalForInbound:  form.values.requireManagerApprovalForInbound,
        crossWarehouseSourcing: {
          ...(prev?.crossWarehouseSourcing ?? {}),
          enabled: form.values.crossWarehouseSourcingEnabled,
        },
      },
    })
  }

  return (
    <Stack gap="md" maw={620}>
      <Group gap="sm" align="center">
        <TrayArrowDownIcon size={22} />
        <Text fw={600} size="lg">{t("inboundFlow.title")}</Text>
      </Group>

      <SettingsFields code={SETTINGS_WRITE.governance}>
        <Card withBorder radius="md" p="md">
          <Stack gap="sm">
            <Title order={5}>{t("inboundFlow.internalCard.title")}</Title>
            <Switch
              label={t("inboundFlow.internalCard.fields.enabled.label")}
              description={t("inboundFlow.internalCard.fields.enabled.description")}
              checked={form.values.crossWarehouseSourcingEnabled}
              onChange={(e) => form.setFieldValue("crossWarehouseSourcingEnabled", e.currentTarget.checked)}
            />
            <Switch
              label={t("inboundFlow.internalCard.fields.autoCreateStoOnShortage.label")}
              description={t("inboundFlow.internalCard.fields.autoCreateStoOnShortage.description")}
              checked={form.values.autoCreateStoOnShortage}
              onChange={(e) => form.setFieldValue("autoCreateStoOnShortage", e.currentTarget.checked)}
            />
          </Stack>
        </Card>

        <Card withBorder radius="md" p="md">
          <Stack gap="sm">
            <Title order={5}>
              {t("inboundFlow.externalCard.title")}
              <HelpIcon helpKey="order.settings.section.inbound-external" variant="section" />
            </Title>
            <Switch
              label={t("inboundFlow.externalCard.fields.enabled.label")}
              description={t("inboundFlow.externalCard.fields.enabled.description")}
              checked={form.values.externalPickupEnabled}
              onChange={(e) => form.setFieldValue("externalPickupEnabled", e.currentTarget.checked)}
            />
            <Switch
              label={t("inboundFlow.externalCard.fields.catalogAutoCreateOnReceipt.label")}
              description={t("inboundFlow.externalCard.fields.catalogAutoCreateOnReceipt.description")}
              checked={form.values.catalogAutoCreateOnReceipt}
              onChange={(e) => form.setFieldValue("catalogAutoCreateOnReceipt", e.currentTarget.checked)}
            />
            <Switch
              label={t("inboundFlow.externalCard.fields.requireSenderVerifyAtExternalParty.label")}
              description={t("inboundFlow.externalCard.fields.requireSenderVerifyAtExternalParty.description")}
              checked={form.values.requireSenderVerifyAtExternalParty}
              onChange={(e) => form.setFieldValue("requireSenderVerifyAtExternalParty", e.currentTarget.checked)}
            />
            <Switch
              label={t("inboundFlow.externalCard.fields.requireManagerApprovalForInbound.label")}
              description={t("inboundFlow.externalCard.fields.requireManagerApprovalForInbound.description")}
              checked={form.values.requireManagerApprovalForInbound}
              onChange={(e) => form.setFieldValue("requireManagerApprovalForInbound", e.currentTarget.checked)}
            />
          </Stack>
        </Card>
      </SettingsFields>

      <Group justify="flex-end">
        <SettingsButton code={SETTINGS_WRITE.governance} helpKey="order.settings.action.save-inbound-flow" loading={mutations.submitting} onClick={handleSave}>
          {t("common.save")}
        </SettingsButton>
      </Group>
    </Stack>
  )
}
